import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insideFeatures, insidePages } from '../data/content';
import { OFFER_ANCHOR } from '../data/config';
import { CheckItem } from './CheckItem';
import { CtaButton } from './CtaButton';
const AUTOPLAY_MS = 4000;
export function InsideLookSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragged = useRef(false);
  const total = insidePages.length;
  const go = (dir: 1 | -1) => setIndex(i => (i + dir + total) % total);
  const page = insidePages[index];
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setIndex(i => (i + 1) % total), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, paused, total]);
  return <section className="bg-slate-100 px-4 py-6 text-center md:px-12 md:py-8" data-id="element-97" data-mp="12to7sqg20s0a">
      <h2 className="text-[24px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-blue-950 md:text-[32px]" data-id="element-98" data-mp="12vs9d1qhdexe">
        Echá un vistazo al interior
      </h2>
      <p className="mx-auto mt-1.5 max-w-[560px] text-[14px] font-bold uppercase leading-tight text-teal-700 md:text-[16px]" data-id="element-99" data-mp="2s8710i1neq92">
        Cada ejercicio incluye herramientas para su aplicación clínica.
      </p>

      <div className="relative mx-auto mt-4 max-w-[440px]" role="region" aria-roledescription="carrusel" aria-label="Vista previa de páginas" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-100" data-mp="3276d6almh40t">
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-md" data-id="element-101" data-mp="2j9lp35qom9h9">
          <AnimatePresence mode="wait" initial={false} data-id="element-102" data-mp="1lu7szheqmp1m">
            <motion.a key={index} href={OFFER_ANCHOR} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragStart={() => dragged.current = true} onDragEnd={(_, info) => {
            if (info.offset.x < -50) go(1);else if (info.offset.x > 50) go(-1);
            setTimeout(() => dragged.current = false, 0);
          }} onClick={e => dragged.current && e.preventDefault()} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className="block aspect-[4/5] w-full cursor-grab touch-pan-y active:cursor-grabbing" aria-label={`Vista ${index + 1} de ${total} — ir a la oferta especial`} data-id="element-103" data-mp="1lrllfhruk9je">
              <img src={page.image} alt={page.alt} className="h-full w-full object-contain" draggable={false} data-id="element-104" data-mp="2l6ldjk0ptuz3" />
            </motion.a>
          </AnimatePresence>
        </div>
        <button type="button" onClick={() => go(-1)} aria-label="Vista anterior" className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-950 shadow-md transition-colors duration-150 hover:text-teal-700" data-id="element-105" data-mp="2g78t15xbgzzo">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-106" data-mp="3u30b1eg3xw7k" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Vista siguiente" className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-950 shadow-md transition-colors duration-150 hover:text-teal-700" data-id="element-107" data-mp="3u5mlpmyaoru0">
          <ChevronRightIcon className="h-5 w-5" data-id="element-108" data-mp="gw72a7nlqquc" />
        </button>
      </div>

      <div className="mt-3 flex justify-center gap-1.5" role="tablist" aria-label="Elegir vista" data-id="element-109" data-mp="14mukblr8ewum">
        {insidePages.map((p, i) => <button key={p.image} type="button" role="tab" onClick={() => setIndex(i)} aria-label={`Mostrar vista ${i + 1}`} aria-selected={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-5 bg-blue-950' : 'w-2 bg-slate-300'}`} data-id="element-110" data-mp="19e2uik0dym8u" data-mpi={i} />)}
      </div>

      <ul className="mx-auto mt-5 grid max-w-[560px] gap-x-6 gap-y-1 text-left text-[14px] leading-[1.4] text-slate-600 sm:grid-cols-2 md:text-[15px]" data-id="element-111" data-mp="32zfrr2hizv86">
        {insideFeatures.map(f => <CheckItem key={f} data-id="element-112" data-mp="3vo4o6o7mzcdl">{f}</CheckItem>)}
      </ul>

      <p className="mx-auto mt-4 max-w-[580px] text-[14px] font-bold leading-snug text-blue-950 md:text-[15px]" data-id="element-113" data-mp="3qtqx2y00breq">
        No son solamente imágenes de ejercicios. Son recursos organizados para consultar, seleccionar y adaptar a cada
        paciente.
      </p>
      <div className="mt-3" data-id="element-114" data-mp="ifiaq7o2vwv0">
        <CtaButton mode="scroll" subtext="Ver la oferta especial de lanzamiento" data-id="element-115" data-mp="iegpxbh9fw03">
          ¡Quiero estas fichas en mi consultorio!
        </CtaButton>
      </div>
    </section>;
}